import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { defer, map, Observable } from 'rxjs';
import { Avatar, IFile } from '../modules/shared/interfaces/files.interface';
import { saveAs } from 'file-saver';

export type FileUploadContext =
  | { routingManaged: true; requestId: string }
  | { routingManaged?: false; requestId?: never };

type UploadFileResponse = Omit<IFile, 'fileSize'> & {
  fileSize?: number;
  size?: number;
};

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  constructor(private http: HttpClient) {}

  deleteFile(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/files/delete-file/${id}`);
  }

  deleteFileByUrl(filePath: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/files/delete-file-by-url`, {
      body: {
        filePath,
      },
    });
  }

  getDownloadFileUrl(fileId: string): string {
    return `${environment.apiUrl}/files/download-file/${fileId}`;
  }

  downloadFileByUrl(url: string, filename: string) {
    this.http.get(url, { responseType: 'blob'}).subscribe(blob => {
      saveAs(blob, filename);
    });
    return;
  }

  uploadFile(file: File, context: FileUploadContext = {}): Observable<IFile> {
    return defer(() => {
      const formData = this.createUploadFormData(file, context);

      return this.http
        .post<UploadFileResponse>(`${environment.apiUrl}/files/upload-file`, formData, {
          headers: this.uploadHeaders(),
          responseType: 'json',
        })
        .pipe(map(response => this.normalizeUploadResponse(response)));
    });
  }

  uploadPicture(file: File, width?: number, height?: number): Observable<Avatar> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    if (width) formData.append('width', width.toString());
    if (height) formData.append('height', height.toString());

    const headers = new HttpHeaders({
      Accept: 'application/json; charset=utf-8',
    });

    return this.http.post<Avatar>(`${environment.apiUrl}/files/upload-picture`, formData, {
      headers: headers,
      responseType: 'json',
    });
  }

  uploadFileWithProgress(
    file: File,
    context: FileUploadContext = {}
  ): Observable<HttpEvent<IFile>> {
    return defer(() => {
      const formData = this.createUploadFormData(file, context);

      return this.http
        .post<UploadFileResponse>(`${environment.apiUrl}/files/upload-file`, formData, {
          reportProgress: true,
          responseType: 'json',
          headers: this.uploadHeaders(),
          observe: 'events',
        })
        .pipe(
          map(event => {
            if (event instanceof HttpResponse && event.body) {
              return event.clone<IFile>({ body: this.normalizeUploadResponse(event.body) });
            }
            return event as HttpEvent<IFile>;
          })
        );
    });
  }

  private createUploadFormData(file: File, context: FileUploadContext): FormData {
    const formData = new FormData();
    formData.append('file', file);

    if (context.routingManaged) {
      const requestId = context.requestId.trim();
      if (!requestId) {
        throw new Error('Routing file upload requires a requestId');
      }
      formData.append('requestId', requestId);
    }

    return formData;
  }

  private normalizeUploadResponse(response: UploadFileResponse): IFile {
    const { size, ...file } = response;
    return {
      ...file,
      fileSize: file.fileSize ?? size ?? 0,
    };
  }

  private uploadHeaders(): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json; charset=utf-8',
    });
  }
}
